import React, { useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi2';
import DocumentCard from './DocumentCard';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.03,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 24,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: { duration: 0.2 },
  },
};

export default function DocumentsGrid({
  docs,
  isPreviewable,
  canManageDoc,
  openDetails,
  openPreview,
  openShare,
  openRename,
  openReupload,
  onDelete,
  formatFileSize,
  formatDate,
}) {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [cardsPerView, setCardsPerView] = useState(3);

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 2);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      const w = el.clientWidth;
      if (w >= 1280) setCardsPerView(4);
      else if (w >= 1024) setCardsPerView(3);
      else if (w >= 768) setCardsPerView(2);
      else setCardsPerView(1);
      updateScrollState();
    });
    ro.observe(el);
    updateScrollState();
    return () => ro.disconnect();
  }, [docs.length]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState);
    return () => el.removeEventListener('scroll', updateScrollState);
  }, []);

  const scroll = dir => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.offsetWidth / cardsPerView + 24; // gap included
    el.scrollBy({ left: dir * cardWidth * cardsPerView, behavior: 'smooth' });
  };

  if (!docs?.length) return null;

  return (
    <div className="relative w-full">
      {/* Carousel navigation - desktop */}
      {docs.length > cardsPerView && (
        <>
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: canScrollLeft ? 1 : 0.4 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => scroll(-1)}
            disabled={!canScrollLeft}
            className="absolute -left-4 top-1/2 z-20 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white shadow-lg shadow-gray-200/80 transition-all hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-40 lg:flex"
            aria-label="Previous"
          >
            <HiChevronLeft className="h-6 w-6" />
          </motion.button>
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: canScrollRight ? 1 : 0.4 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => scroll(1)}
            disabled={!canScrollRight}
            className="absolute -right-4 top-1/2 z-20 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-gray-200 bg-white shadow-lg shadow-gray-200/80 transition-all hover:border-blue-300 hover:bg-blue-50 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-40 lg:flex"
            aria-label="Next"
          >
            <HiChevronRight className="h-6 w-6" />
          </motion.button>
        </>
      )}

      {/* Carousel container */}
      <motion.div
        ref={scrollRef}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="overflow-x-auto overflow-y-hidden pb-4 md:pb-6"
        style={{
          scrollSnapType: 'x mandatory',
          scrollPaddingLeft: '1rem',
          scrollPaddingRight: '1rem',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <div className="flex gap-6 px-1 lg:px-2">
          <AnimatePresence mode="popLayout">
            {docs.map(doc => (
              <motion.div
                key={doc.id}
                layout
                variants={itemVariants}
                exit="exit"
                className="min-w-[280px] shrink-0 sm:min-w-[300px] md:min-w-[320px] lg:min-w-[320px]"
                style={{
                  scrollSnapAlign: 'start',
                  scrollSnapStop: 'always',
                }}
              >
                <div className="h-full">
                  <DocumentCard
                    doc={doc}
                    isPreviewable={isPreviewable}
                    canManageDoc={canManageDoc}
                    openDetails={openDetails}
                    openPreview={openPreview}
                    openShare={openShare}
                    openRename={openRename}
                    openReupload={openReupload}
                    onDelete={onDelete}
                    formatFileSize={formatFileSize}
                    formatDate={formatDate}
                  />
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* Mobile swipe hint */}
      <p className="mt-2 text-center text-xs text-gray-500 sm:hidden">Swipe to browse documents</p>
    </div>
  );
}
